import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/app/environments/environment';
import {
  ApiResponse,
  Conversation,
  ConversationsResponse,
  CreateOneOnOneConversationRequest,
  CreateEventGroupConversationRequest,
  SendMessageRequest,
  EditMessageRequest,
  MarkReadRequest,
  DeleteMessageRequest,
  DeleteConversationRequest,
  MessagesResponse,
  Message,
  OnlineStatusResponse,
  PresenceData,
  SharedDocument,
  SharedDocumentsResponse
} from '../interface/chat.interface';

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private readonly apiUrl = environment.apiUrl;
  private readonly baseRoute = 'chat';
  private readonly skipLoadingHeaders = new HttpHeaders({ 'Skip-Loading': 'true' });

  constructor(private readonly http: HttpClient) {}

  private getUrl(route: string): string {
    return `${this.apiUrl}/${this.baseRoute}/${route}`;
  }

  /**
   * Create one-on-one conversation
   */
  createOneOnOneConversation(payload: CreateOneOnOneConversationRequest): Observable<ApiResponse<{ conversation: Conversation }>> {
    return this.http.post<ApiResponse<{ conversation: Conversation }>>(
      this.getUrl('conversation/one-on-one'),
      payload,
      { headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Create event group conversation
   */
  createEventGroupConversation(payload: CreateEventGroupConversationRequest): Observable<ApiResponse<{ conversation: Conversation }>> {
    return this.http.post<ApiResponse<{ conversation: Conversation }>>(
      this.getUrl('conversation/event-group'),
      payload,
      { headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Send message (with optional file attachments)
   * If files are present, sends FormData; otherwise sends JSON
   */
  sendMessage(payload: SendMessageRequest | FormData): Observable<ApiResponse<{ message: Message }>> {
    return this.http.post<ApiResponse<{ message: Message }>>(
      this.getUrl('message'),
      payload,
      { headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Get conversations with pagination and filters
   */
  getConversations(params?: HttpParams): Observable<ApiResponse<ConversationsResponse>> {
    return this.http.get<ApiResponse<ConversationsResponse>>(
      this.getUrl('conversations'),
      { params, headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Get messages for a conversation
   */
  getMessages(conversationId: number, params?: HttpParams): Observable<ApiResponse<MessagesResponse>> {
    const queryParams = params || new HttpParams();
    const finalParams = queryParams.set('conversation_id', conversationId.toString());
    
    return this.http.get<ApiResponse<MessagesResponse>>(
      this.getUrl('messages'),
      { params: finalParams, headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Mark messages as read
   */
  markMessagesAsRead(payload: MarkReadRequest): Observable<ApiResponse<{ updated_count: number }>> {
    return this.http.post<ApiResponse<{ updated_count: number }>>(
      this.getUrl('messages/mark-read'),
      payload,
      { headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Edit message
   */
  editMessage(payload: EditMessageRequest): Observable<ApiResponse<{ message: Message }>> {
    return this.http.put<ApiResponse<{ message: Message }>>(
      this.getUrl('message'),
      payload,
      { headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Delete message
   */
  deleteMessage(payload: DeleteMessageRequest): Observable<ApiResponse<{ message_id: number }>> {
    return this.http.delete<ApiResponse<{ message_id: number }>>(
      this.getUrl('message'),
      { body: payload, headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Delete conversation
   */
  deleteConversation(payload: DeleteConversationRequest): Observable<ApiResponse<{ conversation_id: number }>> {
    return this.http.delete<ApiResponse<{ conversation_id: number }>>(
      this.getUrl('conversation'),
      { body: payload, headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Get online status for multiple users/vendors (Admin only)
   */
  getOnlineStatus(userIds?: number[], vendorIds?: number[]): Observable<ApiResponse<OnlineStatusResponse>> {
    let params = new HttpParams();
    
    if (userIds && userIds.length > 0) {
      params = params.set('user_ids', userIds.join(','));
    }
    
    if (vendorIds && vendorIds.length > 0) {
      params = params.set('vendor_ids', vendorIds.join(','));
    }
    
    return this.http.get<ApiResponse<OnlineStatusResponse>>(
      this.getUrl('presence/status'),
      { params, headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Get presence data for a specific user/vendor (Admin only)
   */
  getPresenceData(userType: 'admin' | 'vendor', userId: number): Observable<ApiResponse<PresenceData>> {
    return this.http.get<ApiResponse<PresenceData>>(
      this.getUrl(`presence/${userType}/${userId}`),
      { headers: this.skipLoadingHeaders }
    );
  }

  /**
   * Get shared documents/files for a conversation
   */
  getSharedDocuments(conversationId: number, params?: HttpParams): Observable<ApiResponse<SharedDocumentsResponse>> {
    return this.http.get<ApiResponse<SharedDocumentsResponse>>(
      `${this.apiUrl}/${this.baseRoute}/conversations/${conversationId}/documents`,
      { params, headers: this.skipLoadingHeaders }
    );
  }
}

